import { Box, Button, Container, Stack, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { setCustomerDetail } from '../store/state/customerDetailSlice';
import Loading from './Loading';
import {
  useFindCustomerDetailByIdMutation,
  useGetAllCustomerDetailMutation,
  useSaveCustomerDetailMutation,
  useUpdateCustomerDetailMutation,
} from '../store/api/customerDetailApi';

type Props = {};

const customerSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please enter full name')
    .matches(/^[a-zA-Z\s.-]+$/, { message: 'Name must be alphabetic' }),
  phoneNo: Yup.string()
    .required('Phone No is required')
    .min(10, 'Minimum 10 digits allowed')
    .max(10, 'Maximum 10 digits allowed'),
  email: Yup.string().email('Invalid email').required('Email is required'),
});

const ResponsiveBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const FormikBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '20px',
  },
}));

const AddOrUpdateCustomer = (props: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    name: '',
    phoneNo: 0,
    email: '',
  });
  const { state }: any = useLocation();
  const token = JSON.parse(localStorage.getItem('login') || '{}').token;
  const [getAllCustomerDetail, getAllCustomerDetailResponse] = useGetAllCustomerDetailMutation();
  const [saveCustomerDetail, saveCustomerDetailResponse] = useSaveCustomerDetailMutation();
  const [updateCustomerDetail, updateCustomerDetailResponse] = useUpdateCustomerDetailMutation();
  const [findById, findByIdResponse] = useFindCustomerDetailByIdMutation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [name, setName] = useState('');

  useEffect(() => {
    if (saveCustomerDetailResponse.isSuccess || updateCustomerDetailResponse.isSuccess) {
      getAllCustomerDetail({
        token: token,
        payload: {
          offset: rowsPerPage,
          page: page,
          name: name,
        },
      });
    }

    if (saveCustomerDetailResponse.isError || updateCustomerDetailResponse.isError) {
      let errorData: any = saveCustomerDetailResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [
    saveCustomerDetailResponse.isSuccess,
    updateCustomerDetailResponse.isSuccess,
    saveCustomerDetailResponse.isError,
    updateCustomerDetailResponse.isError,
  ]);

  // update store
  useEffect(() => {
    if (getAllCustomerDetailResponse.isSuccess) {
      dispatch(setCustomerDetail(getAllCustomerDetailResponse.data));
      navigate('/dashboard/customer-details');
    }

    if (getAllCustomerDetailResponse.isError) {
      let errorData: any = getAllCustomerDetailResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [getAllCustomerDetailResponse.isSuccess, getAllCustomerDetailResponse.isError]);

  // set formik initial values
  useEffect(() => {
    if (state.editMode) {
      findById({
        token: token,
        id: state.id,
      });
    }
  }, [state.editMode]);

  useEffect(() => {
    if (findByIdResponse.isSuccess) {
      setInitialValues(() => {
        return {
          name: findByIdResponse.data.name,
          phoneNo: parseInt(findByIdResponse.data.phoneNo) || 0,
          email: findByIdResponse.data.email,
        };
      });
    }
  }, [findByIdResponse.isSuccess]);

  const saveCustomerDetailDataToServer = async (values: any) => {
    await saveCustomerDetail({
      payload: {
        name: values.name,
        phoneNo: values.phoneNo,
        email: values.email,
        password: values.password,
        gstNo: values.gstNo,
        primaryAccountNo: values.primaryAccountNo,
        vatNo: values.vatNo,
        status: true,
      },
      token: token,
    });
  };

  const updateCustomerDetailDataInServer = async (values: any) => {
    await updateCustomerDetail({
      payload: {
        id: state.id,
        userId: findByIdResponse.data.userId,
        name: values.name,
        email: values.email,
        phoneNo: values.phoneNo,
        status: true,
        addressLine1: findByIdResponse.data.addressLine1,
        addressLine2: findByIdResponse.data.addressLine2,
        dob: findByIdResponse.data.dob,
        gender: findByIdResponse.data.gender,
        imageUrl: findByIdResponse.data.imageUrl,
        rewardBalance: findByIdResponse.data.rewardBalance,
        totalPoints: findByIdResponse.data.totalPoints,
        vehicleRegistrationNumber: findByIdResponse.data.vehicleRegistrationNumber,
        merchantId: findByIdResponse.data.merchantId,
      },
      token: token,
    });
  };

  const handleAddNewServiceProvider = (values: any, action: any) => {
    if (state.editMode) {
      updateCustomerDetailDataInServer(values);
    } else {
      saveCustomerDetailDataToServer(values);
    }
    action.resetForm();
  };

  useEffect(() => {
    if (updateCustomerDetailResponse.isSuccess) {
      toast.success('Customer updated successfully');
    }
    if (saveCustomerDetailResponse.isSuccess) {
      toast.success('Customer added successfully');
    }
  }, [updateCustomerDetailResponse.isSuccess, saveCustomerDetailResponse.isSuccess]);

  return (
    <Box>
      <Container>
        <h1>{state.editMode ? 'Edit Customer' : 'Add Customer'}</h1>
        <ResponsiveBox style={{ display: 'flex', paddingTop: '40px' }}>
          <FormikBox style={{ flex: 1, padding: 0 }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleAddNewServiceProvider}
              validationSchema={customerSchema}
            >
              {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, touched, errors }) => (
                <Box>
                  <Box display={'flex'} alignItems={'center'} marginTop={'25px'} marginLeft={'25px'}>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Customer Name
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="name"
                          style={{
                            width: '80%',
                          }}
                          type="text"
                          name="name"
                          label="Enter customer full name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Stack>
                    </Box>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Phone Number
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="phoneNo"
                          style={{
                            width: '80%',
                          }}
                          type="number"
                          name="phoneNo"
                          label="Enter Phone Number"
                          value={values.phoneNo}
                          onChange={handleChange}
                          disabled={state.editMode ? true : false}
                          onBlur={handleBlur}
                          error={Boolean(touched.phoneNo && errors.phoneNo)}
                          helperText={touched.phoneNo && errors.phoneNo}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Box display={'flex'} alignItems={'center'} marginTop={'25px'} marginLeft={'25px'}>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Email Address
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="email"
                          style={{
                            width: '80%',
                          }}
                          type="text"
                          name="email"
                          label="Enter email address"
                          value={values.email}
                          disabled={state.editMode ? true : false}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        navigate('/dashboard/customer-details');
                      }}
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      sx={{ marginRight: '10px' }}
                      size="large"
                    >
                      Cancel
                    </Button>
                    <Button
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      variant="contained"
                      onClick={() => {
                        handleSubmit();
                      }}
                      size="large"
                      type="submit"
                    >
                      {state.editMode ? 'Update' : 'Add'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Formik>
          </FormikBox>
        </ResponsiveBox>
      </Container>
      {saveCustomerDetailResponse.isLoading || updateCustomerDetailResponse.isLoading || findByIdResponse.isLoading ? (
        <Loading />
      ) : null}
    </Box>
  );
};

export default AddOrUpdateCustomer;
